import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Lock, ShieldCheck, Zap, BookOpen } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Eyebrow } from "@/components/ui-kit";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { IMAGES } from "@/lib/content";

/**
 * /checkout — Stripe-hosted checkout redirect.
 *
 * Amounts are defined SERVER-SIDE (PACKAGES["the_shift"] = $19). The frontend
 * only sends `package_id` + `origin_url` — never the price. On click we POST
 * to /api/payments/checkout, get a Stripe session URL, and redirect. Success
 * lands on /payment/success (polls status). Cancel lands on /payment/cancel.
 */
export default function Checkout() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const startCheckout = async () => {
    setLoading(true);
    setError("");
    track.cta("get_the_shift_stripe", "checkout_page");
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: "the_shift",
        origin_url: window.location.origin,
      });
      if (!data?.checkout_url) throw new Error("No checkout URL returned");
      window.location.href = data.checkout_url;
    } catch (err) {
      setLoading(false);
      setError(
        err.response?.data?.detail ||
          err.message ||
          "We couldn't reach the payment provider. Please try again in a moment.",
      );
    }
  };

  return (
    <div data-testid="checkout-page">
      <Seo
        title="Get The Shift — $19"
        description="Get your copy of Employee 2 Employer: The Shift by Don Orr Jr. Instant digital access."
        path="/checkout"
      />

      <section className="relative bg-black noise pt-36 pb-24 lg:pt-44 lg:pb-32 overflow-hidden">
        <div className="relative z-10 max-w-[1180px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          {/* LEFT — Book */}
          <div className="lg:col-span-5 flex items-start justify-center order-2 lg:order-1">
            <img
              src={IMAGES.book}
              alt="The Shift — book cover"
              loading="eager"
              className="max-h-[560px] w-auto animate-float-book"
              style={{
                filter:
                  "drop-shadow(0 55px 45px rgba(0,0,0,0.55)) drop-shadow(0 20px 25px rgba(0,0,0,0.35))",
              }}
            />
          </div>

          {/* RIGHT — Offer + CTA */}
          <div className="lg:col-span-7 order-1 lg:order-2">
            <Reveal>
              <Eyebrow>Your Copy of The Shift</Eyebrow>
            </Reveal>
            <Reveal delay={80}>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-[4rem] text-white mt-7 leading-[1.04] tracking-tight">
                Break the employee mindset.{" "}
                <span className="text-[#D4AF37] italic">Build what lasts.</span>
              </h1>
            </Reveal>
            <Reveal delay={140}>
              <p className="font-body text-lg text-white/60 font-light mt-8 max-w-xl leading-relaxed">
                Employee 2 Employer: The Shift by Don Orr Jr. The foundational book of the
                E2E movement — a framework for identity, mindset, ownership, and legacy.
              </p>
            </Reveal>

            <Reveal delay={200}>
              <div className="mt-10 flex flex-wrap items-baseline gap-5 border-t border-b border-white/10 py-6">
                <span
                  className="font-display text-5xl text-[#D4AF37]"
                  data-testid="checkout-price"
                >
                  $19
                </span>
                <span className="font-body text-sm tracking-[0.18em] uppercase text-white/50">
                  Digital Book · Instant Access
                </span>
              </div>
            </Reveal>

            <Reveal delay={260}>
              <div className="mt-10 space-y-4">
                <button
                  onClick={startCheckout}
                  disabled={loading}
                  data-testid="checkout-buy-button"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#D4AF37] hover:bg-[#E6C75A] disabled:opacity-60 text-black font-body font-medium text-[15px] px-10 py-5 transition-all duration-300 group"
                >
                  {loading ? "Opening secure checkout…" : "Get The Shift — $19"}
                  <ArrowRight
                    size={16}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </button>
                {error && (
                  <p
                    className="font-body text-sm text-red-400"
                    data-testid="checkout-error"
                    role="alert"
                  >
                    {error}
                  </p>
                )}
                <div className="pt-2 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-white/40 font-body">
                  <span className="inline-flex items-center gap-2">
                    <Lock size={12} className="text-[#D4AF37]/70" />
                    Secure checkout by Stripe
                  </span>
                  <span>·</span>
                  <span>Instant digital delivery by email</span>
                </div>
              </div>
            </Reveal>

            <Reveal delay={340}>
              <p className="font-body text-[13px] text-white/45 leading-relaxed mt-8 max-w-md">
                Not ready yet?{" "}
                <Link
                  to="/preview"
                  className="text-white/75 underline hover:text-[#D4AF37]"
                >
                  Read Chapter One first
                </Link>{" "}
                — it&apos;s free, no email required.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHAT YOU'RE GETTING */}
      <section className="bg-white text-black py-24 lg:py-32">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
          {[
            {
              icon: BookOpen,
              title: "A foundational read",
              body:
                "Identity, mindset, ownership, service, and stewardship — the principles every builder needs before scaling anything.",
            },
            {
              icon: ShieldCheck,
              title: "Not another course",
              body:
                "A book you can highlight, share, revisit, and hand to someone you love. The beginning of a journey, not the whole of it.",
            },
            {
              icon: Zap,
              title: "Instant delivery",
              body:
                "The moment payment clears, your copy lands in your inbox. Read on your phone, tablet, laptop — anywhere the shift needs to happen.",
            },
          ].map((c) => (
            <div key={c.title} className="border-t border-black/10 pt-6">
              <c.icon size={20} className="text-[#B8952E]" strokeWidth={1.6} />
              <h3 className="font-display text-2xl mt-4 leading-snug">{c.title}</h3>
              <p className="font-body text-black/65 mt-4 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
